import type { InterventionStatus } from '@arhs-fleetback/types/entities/interventions';
import { FinalInvoiceStatus, InterventionEventType } from '@arhs-fleetback/types/entities/interventions';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import {
  TirePositionEnum,
  TireSeasonEnum
} from 'components/interventions/detail/Works/components/TireReport/constants';
import { ENTITY_TYPE } from 'modules/app/constants';
import type { ModeEnum } from 'modules/global/typing/enums/modes.enum';
import { createDictionary } from 'utils/ObjectUtils';

import { INTERVENTION_ENTITIES_API_URL } from './ApiConstants';
import { AFTER_SALES, PARTS as PARTS_EXTENSION, SALES as SALES_EXTENSION } from './ExtensionConstants';
import { TECHNICIAN, SALES, PARTS } from './ModeConstants';

export const INTERVENTION_MODE = {
  TECHNICIAN,
  SALES,
  PARTS
} as const satisfies Partial<Record<ModeEnum, ModeEnum>>;

export const INTERVENTION_ACCEPT_ORIGIN = {
  PHONE: 'PHONE',
  SMS: 'SMS',
  EMAIL: 'EMAIL',
  VOICE: 'VOICE',
  MAINTENANCE_CONTRACT: 'MAINTENANCE_CONTRACT',
  LEASING: 'LEASING',
  WARRANTY: 'WARRANTY',
  OTHER: 'OTHER'
};

export const ORIGIN_OPTIONS = [
  { value: '', label: <FormattedMessage id="choose" defaultMessage="Please choose..." /> },
  ...Object.values(INTERVENTION_ACCEPT_ORIGIN).map(origin => ({
    value: origin,
    label: (
      <FormattedMessage
        id={`page.fiche.acceptManuallyModal.reason.${origin.toLowerCase()}`}
        defaultMessage={origin.toLowerCase()}
      />
    )
  }))
];

export const ANSWERED = 'ANSWERED';
export const OPEN = 'OPEN';
export const CLOSED = 'CLOSED';
export const READ = 'READ';
export const SENT = 'SENT';
export const TO_BE_SENT = 'TO_BE_SENT';
// Mobile interventions drafts
// Should not be visible to users
export const IN_PROGRESS = 'IN_PROGRESS';
export const DELETED = 'DELETED';

// ⚠️ Respect the intervention status workflow !
export const ALL_STATUS = [OPEN, TO_BE_SENT, IN_PROGRESS, SENT, READ, ANSWERED, CLOSED, DELETED];

export const STATUSES = {
  ANSWERED,
  OPEN,
  CLOSED,
  READ,
  SENT,
  TO_BE_SENT,
  IN_PROGRESS,
  DELETED
};

export const MIN_DAY_BEFORE_SEND_REMINDER = 0;

export const MAX_DAY_BEFORE_SEND_REMINDER = 150;

export const DEFAULT_REMINDER_VALUES = {
  daysBeforeSendReminder: 14,
  daysBeforeSendSeverityLow: 30,
  daysBeforeSendSeverityHigh: 14
};

export const { ACCEPTED, CUSTOMER_INFO_UPDATED } = InterventionEventType;

const {
  ASSIGNEE_CHANGED,
  CHECKLIST_IMPORT,
  CREATE,
  CUSTOM_STEP_CHANGED,
  DUPLICATED,
  FINAL_INVOICE_CREATED,
  INTEGRATION_SENT,
  KANBAN_STATUS_UPDATED,
  PAYMENT_DEPOSIT_SUCCESS,
  PAYMENT_DEPOSIT_FAILED,
  PAYMENT_FAILED,
  PAYMENT_SUCCESS,
  REJECTED,
  REOPENED,
  SELF_SERVICE_ANSWERED,
  SELF_SERVICE_ANSWER_REMOVED,
  SELF_SERVICE_BREAKDOWN_CREATED,
  SELF_SERVICE_SENT,
  TAKE_APPOINTMENT,
  TRACK_INSTRUCTION_SENT,
  UPDATE,
  INTERNAL_MESSAGE_UPDATED
} = InterventionEventType;

/**
 * TODO: these 4 events are missing from @arhs-fleetback/types `InterventionEventType` (0.0.69) — request
 * the package add them, then drop these local literals in favor of the package enum.
 */
export const ADDITIONAL_CUSTOMER_INFO_UPDATED = 'ADDITIONAL_CUSTOMER_INFO_UPDATED';
export const FIELD_OVERRIDE = 'FIELD_OVERRIDE';
const DOCUMENT_SIGNED = 'DOCUMENT_SIGNED';
const SERVICE_ADVISOR_CHANGED = 'SERVICE_ADVISOR_CHANGED';

export const BREAKDOWN_SUBTYPE = 'KIOSK_BREAKDOWN';

const { ACCEPTED_POLICY, FORCED_MESSAGE_CREATED } = InterventionEventType;

/** Kiosk slots events */
const KIOSK_SLOT_EVENT = {
  SELF_SERVICE_SLOT_FILLED: InterventionEventType.SELF_SERVICE_SLOT_FILLED,
  SELF_SERVICE_SLOT_OPENED: InterventionEventType.SELF_SERVICE_SLOT_OPENED,
  SELF_SERVICE_SLOT_EMPTIED: InterventionEventType.SELF_SERVICE_SLOT_EMPTIED,
  SELF_SERVICE_SLOT_CLOSED: InterventionEventType.SELF_SERVICE_SLOT_CLOSED
};

export const KIOSK_SLOT_EVENTS = Object.values(KIOSK_SLOT_EVENT);

/** Phone agent (Flowlyne) call outcome events. */
const PHONE_AGENT_CALL_EVENT = {
  SELF_SERVICE_PHONE_AGENT_CALL_ANSWERED: InterventionEventType.SELF_SERVICE_PHONE_AGENT_CALL_ANSWERED,
  SELF_SERVICE_PHONE_AGENT_CALL_FAILED: InterventionEventType.SELF_SERVICE_PHONE_AGENT_CALL_FAILED,
  SELF_SERVICE_PHONE_AGENT_CALL_NOT_ANSWERED: InterventionEventType.SELF_SERVICE_PHONE_AGENT_CALL_NOT_ANSWERED
};

export const PHONE_AGENT_CALL_EVENTS = Object.values(PHONE_AGENT_CALL_EVENT);

export const MOBILITY_EVENTS = {
  MOBILITY_CONTRACT_SIGNED: InterventionEventType.MOBILITY_CONTRACT_SIGNED,
  MOBILITY_DELIVERY_DOCUMENT_SIGNED: InterventionEventType.MOBILITY_DELIVERY_DOCUMENT_SIGNED,
  MOBILITY_DOCUMENT_ANSWERED: InterventionEventType.MOBILITY_DOCUMENT_ANSWERED,
  MOBILITY_DOCUMENT_MEDIA_ADDED: InterventionEventType.MOBILITY_DOCUMENT_MEDIA_ADDED,
  MOBILITY_REFUSED: InterventionEventType.MOBILITY_REFUSED
};

const { WORK_ORDER_SIGNED } = InterventionEventType;

export const ALL_EVENTS = {
  ACCEPTED,
  ADDITIONAL_CUSTOMER_INFO_UPDATED,
  ASSIGNEE_CHANGED,
  ANSWERED,
  CHECKLIST_IMPORT,
  CLOSED,
  CREATE,
  CUSTOM_STEP_CHANGED,
  CUSTOMER_INFO_UPDATED,
  DELETED,
  DUPLICATED,
  DOCUMENT_SIGNED,
  FIELD_OVERRIDE,
  FINAL_INVOICE_CREATED,
  INTEGRATION_SENT,
  KANBAN_STATUS_UPDATED,
  PAYMENT_DEPOSIT_SUCCESS,
  PAYMENT_DEPOSIT_FAILED,
  PAYMENT_FAILED,
  PAYMENT_SUCCESS,
  READ,
  REJECTED,
  REOPENED,
  SELF_SERVICE_ANSWERED,
  SELF_SERVICE_ANSWER_REMOVED,
  SELF_SERVICE_SENT,
  SELF_SERVICE_BREAKDOWN_CREATED,
  SENT,
  TAKE_APPOINTMENT,
  TRACK_INSTRUCTION_SENT,
  UPDATE,
  INTERNAL_MESSAGE_UPDATED,
  ACCEPTED_POLICY,
  WORK_ORDER_SIGNED,
  SERVICE_ADVISOR_CHANGED,
  FORCED_MESSAGE_CREATED,
  /** Kiosk slots events */
  ...KIOSK_SLOT_EVENT,
  /** Phone agent call outcome events */
  ...PHONE_AGENT_CALL_EVENT,
  ...MOBILITY_EVENTS
};

export const DEFAULT_STATUS = ALL_STATUS.filter(
  status => ![IN_PROGRESS, DELETED].includes(status),
) as InterventionStatus[];

export const OPINION = {
  YES: 'YES',
  NO: 'NO',
  ABSOLUTELY: 'ABSOLUTELY'
};

export const DUPLICATE_MODAL_TYPE = {
  DUPLICATE: 'DUPLICATE',
  SEND_AS_MESSAGE: 'SEND_AS_MESSAGE',
  BODYWORK: 'BODYWORK'
};

export const PREDEFINED_MESSAGES_MODES = {
  DEFAULT: 'default',
  NONE: 'none'
};

export const DATE_TYPE = {
  CURRENT: 'CURRENT',
  PLANNED: 'PLANNED',
  SPECIFIC: 'SPECIFIC'
};

export const POLLING_DEBOUNCE = 300000;

export const MAX_DEFAULT_MESSAGE_LENGTH = 4800;

export const ATTACHMENTS_TYPE = {
  MEDIAS: 'MEDIAS',
  DOCUMENTS: 'DOCUMENTS',
  CHAT: 'CHAT'
};

export const SETTLED_FINAL_INVOICE_STATUS: FinalInvoiceStatus[] = [
  FinalInvoiceStatus.PAID,
  FinalInvoiceStatus.PRE_AUTHORIZED
];

export const IMPORT_WORK_TYPE = {
  NEW_WORK: 'NEW_WORK',
  EXISTING_WORK: 'EXISTING_WORK'
};

export const KANBAN_COLUMNS_INTL = {
  CHECK_OUT_COMPLETED: ['checkoutCompleted', 'Check-out completed'],
  CHECK_OUT_READY: ['checkoutReady', 'Check-out ready'],
  IN_HOUSE: ['inHouse', 'In house'],
  CHECK_IN_COMPLETED: ['checkinCompleted', 'Check-in completed'],
  HOME_CHECK_IN_COMPLETED: ['homeCheckinCompleted', 'Home check-in completed'],
  DEFAULT: ['default', "Today's appointments"]
};

export const INTERVENTION_PAYMENT = {
  DEPOSIT: 'DEPOSIT',
  FINAL_INVOICE: 'FINAL_INVOICE',
  FULL: 'FULL'
};

export const INTERVENTION_MODE_TO_EXTENSION = {
  [SALES]: SALES_EXTENSION,
  [PARTS]: PARTS_EXTENSION,
  [TECHNICIAN]: AFTER_SALES
};

export const MODE_ENTITY = {
  [SALES]: 'sales',
  [PARTS]: 'parts',
  [TECHNICIAN]: 'afterSales'
};

export const INTERVENTIONS_API_URL = {
  [SALES]: `${INTERVENTION_ENTITIES_API_URL}/${SALES}`,
  [TECHNICIAN]: `${INTERVENTION_ENTITIES_API_URL}/${TECHNICIAN}`,
  [PARTS]: `${INTERVENTION_ENTITIES_API_URL}/${PARTS}`
};

// KEEP ORDER
export const FOREIGN_ENTITY_TYPE = {
  CAMPAIGN: 'campaign',
  APPOINTMENT: 'appointment',
  PARTS_INTERVENTION: 'partsIntervention',
  SELF_SERVICE: 'selfService',
  CAMPAIGN_FILE: 'campaignFile',
  INTERVENTION: 'intervention',
  CONVERSATION: 'conversation'
};

export const ENTITY_TYPE_BY_FOREIGN_ENTITY_TYPE = {
  [FOREIGN_ENTITY_TYPE.APPOINTMENT]: ENTITY_TYPE.CHECKLIST_INTERVENTION,
  [FOREIGN_ENTITY_TYPE.INTERVENTION]: ENTITY_TYPE.CHECKLIST_INTERVENTION,
  [FOREIGN_ENTITY_TYPE.PARTS_INTERVENTION]: ENTITY_TYPE.CHECKLIST_PARTS_INTERVENTION,
  [FOREIGN_ENTITY_TYPE.SELF_SERVICE]: ENTITY_TYPE.CHECKLIST_SELF_SERVICE,
  [FOREIGN_ENTITY_TYPE.CAMPAIGN_FILE]: ENTITY_TYPE.CHECKLIST_CAMPAIGN,
  [FOREIGN_ENTITY_TYPE.CAMPAIGN]: ENTITY_TYPE.CHECKLIST_CAMPAIGN
};

export const FOREIGN_ENTITY_TYPES = Object.values(FOREIGN_ENTITY_TYPE);

export const FRONT_TIRES = [TirePositionEnum.FRONT_LEFT, TirePositionEnum.FRONT_RIGHT];

export const BACK_TIRES = [TirePositionEnum.REAR_RIGHT, TirePositionEnum.REAR_LEFT];

export const TIRE_ORDER = [...FRONT_TIRES, ...BACK_TIRES];

export const TIRE_TYPE_DEFAULT = TireSeasonEnum.UNSPECIFIED;

export const REMINDER_TIRE_TYPES = [TireSeasonEnum.SUMMER, TireSeasonEnum.WINTER];

export const REFERENCE_MIN_LENGTH = 1;

export const REFERENCE_MAX_LENGTH = 80;

export const ANSWERED_PANE_TITLE = createDictionary(
  {
    [SALES]: <FormattedMessage id="page.fiche.offer.title" defaultMessage="Sales offer" />,
    [PARTS]: <FormattedMessage id="page.fiche.parts.title" defaultMessage="Parts offer" />
  },
  <FormattedMessage id="page.fiche.form.title" defaultMessage="Work order" />
);

export const ANSWERED_SECTION_TITLE = createDictionary(
  {
    [TECHNICIAN]: <FormattedMessage id="workOrder.title" defaultMessage="Work order" />
  },
  <FormattedMessage id="page.configurator.newOffer.actions.purchaseOrder" defaultMessage="Purchase order" />
);

export const DATA_TYPE_LABEL = {
  ALL: <FormattedMessage id="statistics_dealers.interventions_to_display.all" defaultMessage="All" />,
  [DATE_TYPE.CURRENT]: <FormattedMessage id="page.home.filters.schedulable.current" defaultMessage="Current" />,
  [DATE_TYPE.PLANNED]: <FormattedMessage id="page.home.filters.schedulable.planned" defaultMessage="Planned" />,
  [DATE_TYPE.SPECIFIC]: <FormattedMessage id="page.home.filters.schedulable.specific" defaultMessage="Specific" />
};

export const ERROR_NOTIFICATION = createDictionary(
  {
    noExportableMedia: {
      id: 'page.fiche.infomediaries.noExportableMedia',
      defaultMessage: 'Cannot export private media. Only public media can be exported.'
    }
  },
  {
    id: 'http.error.message',
    defaultMessage:
      'Oops! Something went wrong. Please try again or contact the administrator if the problem persists.'
  }
);

export const ERROR_MATCHERS = {
  noExportableMedia: ({ errorCode, errorMessage }: { errorCode?: number; errorMessage?: string } = {}) =>
    errorCode === 400 && errorMessage?.includes('has no exportable media')
};
